import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, MessageSquare, Hand, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../../shared/services/api";
import JanelaIndicator       from "./JanelaIndicator";
import MessageBubble         from "./MessageBubble";
import ChatInput             from "./ChatInput";
import MediaUploader         from "./MediaUploader";
import TemplateSelectorModal from "./TemplateSelectorModal";
import LocationSender        from "./LocationSender";
import ContactSender         from "./ContactSender";
import InteractiveBuilder    from "./InteractiveBuilder";
import { listarMensagens, enviarMidia } from "../../services/mensagensApi";
import { listTemplates } from "../../services/templatesApi";
import agentesService from "../../../Agentes/services/agentesService";

async function marcarLido(conversaId) {
  try {
    await api.post(`/whatsapp/meta/conversas/${conversaId}/marcar-lido`);
  } catch (_err) {
    // falha silenciosa — não bloqueia UX
  }
}

// Rótulo de dia estilo WhatsApp: HOJE, ONTEM, ou "13 de maio de 2026".
function rotularDia(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, hoje)) return "HOJE";
  if (sameDay(d, ontem)) return "ONTEM";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function chaveDia(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function DiaSeparador({ iso }) {
  return (
    <div className="flex justify-center my-3 px-3">
      <span className="text-[11px] font-semibold tracking-wider uppercase text-ink-4 bg-surface-2/80 border border-white/[0.06] rounded-full px-3 py-1 select-none">
        {rotularDia(iso)}
      </span>
    </div>
  );
}

/* ── modais disponíveis ── */
const MODAL = {
  NONE:        null,
  MEDIA:       "media",
  TEMPLATE:    "template",
  LOCATION:    "location",
  CONTACT:     "contact",
  INTERACTIVE: "interactive",
};

export default function ChatView({ contaId, conversaId, socket, onVoltar, onModoChange }) {
  const [conversa,    setConversa]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [mensagens,   setMensagens]   = useState([]);
  const [carregando,  setCarregando]  = useState(false);
  const [hasMore,     setHasMore]     = useState(false);
  const [modalAberto, setModalAberto] = useState(MODAL.NONE);
  const [togglandoModo, setTogglandoModo] = useState(false);
  // Cache de templates aprovados pra renderizar o CORPO dos templates enviados
  // (a mensagem `tipo='template'` salva só `templateName + languageCode +
  // parameters`, não o texto do header/body/footer/buttons).
  const [templatesMap, setTemplatesMap] = useState({});

  const listRef      = useRef(null);
  const topo         = useRef(null); // sentinel para infinite scroll up
  const primeiraVez  = useRef(true);
  // Tracka se usuário está perto do fundo. Quando false, NÃO auto-scrolla
  // ao chegar mensagem nova (igual WhatsApp/Telegram) — deixa o usuário
  // ler histórico em paz. Threshold = 120px do bottom.
  const proximoDoFundoRef = useRef(true);
  // Throttle do marcar-lido: dispara no max 1x a cada 3s pra evitar burst
  // de requests quando várias mensagens inbound chegam em sequência.
  const ultimoMarcarLidoRef = useRef(0);

  /* ── rolar para o fundo ── */
  const rolarFundo = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
  }, []);

  /* ── handler de scroll: atualiza ref de "perto do fundo" ── */
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    proximoDoFundoRef.current = dist < 120;
  }, []);

  /* ── marcarLido com throttle (3s) ── */
  const marcarLidoThrottled = useCallback((id) => {
    const agora = Date.now();
    if (agora - ultimoMarcarLidoRef.current < 3000) return;
    ultimoMarcarLidoRef.current = agora;
    marcarLido(id);
  }, []);

  /* ── carregar mensagens (keyset) ── */
  const carregarMensagens = useCallback(async (beforeId = undefined) => {
    if (!conversaId) return;
    setCarregando(true);
    try {
      const { mensagens: lista, hasMore: more } = await listarMensagens(conversaId, {
        limit: 40,
        beforeId: beforeId || null,
      });
      setHasMore(more);
      // Backend retorna DESC (mais recente primeiro) por paginação keyset.
      // UI estilo WhatsApp espera ASC (mais antiga em cima, recente em baixo).
      const ordenadaAsc = [...lista].reverse();
      if (beforeId) {
        // prepend — página de mensagens MAIS ANTIGAS no topo
        setMensagens((prev) => [...ordenadaAsc, ...prev]);
      } else {
        setMensagens(ordenadaAsc);
        primeiraVez.current = true;
      }
    } catch {
      // falha silenciosa — mantém estado anterior
    } finally {
      setCarregando(false);
    }
  }, [conversaId]);

  /* ── busca dados da conversa e mensagens ao abrir ── */
  useEffect(() => {
    if (!conversaId) {
      setConversa(null);
      setMensagens([]);
      return;
    }

    let cancelado = false;
    setLoading(true);
    setMensagens([]);
    setHasMore(false);

    api
      .get(`/whatsapp/meta/conversas/${conversaId}`)
      .then((resp) => {
        if (cancelado) return;
        const dados = resp.data?.data ?? resp.data ?? null;
        setConversa(dados);
      })
      .catch(() => {
        if (!cancelado) setConversa(null);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    carregarMensagens();
    // Marca como lido só na abertura. Inbound seguintes usam o throttled.
    ultimoMarcarLidoRef.current = 0;
    marcarLidoThrottled(conversaId);
    // Volta ao default "perto do fundo" toda vez que troca conversa.
    proximoDoFundoRef.current = true;

    return () => {
      cancelado = true;
    };
  }, [conversaId, carregarMensagens, marcarLidoThrottled]);

  /* ── scroll para o fundo na primeira carga ── */
  useEffect(() => {
    if (mensagens.length > 0 && primeiraVez.current) {
      primeiraVez.current = false;
      rolarFundo();
    }
  }, [mensagens, rolarFundo]);

  /* ── cache de templates aprovados (uma vez por conta) ── */
  useEffect(() => {
    if (!contaId) return;
    let cancelado = false;
    listTemplates(contaId, { status: "APPROVED" })
      .then((data) => {
        if (cancelado) return;
        const lista = Array.isArray(data) ? data : (data?.templates ?? data?.data ?? []);
        const map = {};
        for (const t of lista) {
          if (!t?.name || !t?.language) continue;
          map[`${t.name}::${t.language}`] = t.components || [];
        }
        setTemplatesMap(map);
      })
      .catch(() => { if (!cancelado) setTemplatesMap({}); });
    return () => { cancelado = true; };
  }, [contaId]);

  /* ── IntersectionObserver — carregar mais ao rolar para cima ── */
  useEffect(() => {
    if (!hasMore || !topo.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !carregando && mensagens.length > 0) {
          const primeiroId = mensagens[0]?.id;
          if (primeiroId) carregarMensagens(primeiroId);
        }
      },
      { root: listRef.current, threshold: 0.1 }
    );
    obs.observe(topo.current);
    return () => obs.disconnect();
  }, [hasMore, carregando, mensagens, carregarMensagens]);

  /* ── mensagem enviada via ChatInput ou modal ── */
  function handleMensagemEnviada(nova) {
    // Ignora respostas malformadas (ex: o POST /mensagens retorna só
    // { jobId, enfileirado }, sem tipo — não deve virar bolha).
    if (!nova || !nova.tipo) return;
    setMensagens((prev) => {
      // evita duplicata (socket pode chegar antes ou depois)
      if (prev.some((m) => m.id === nova.id || (nova.wamid && m.wamid === nova.wamid))) return prev;
      return [...prev, nova];
    });
    // Quando É O USUÁRIO enviando, sempre rola pro fundo — ele acabou de
    // mandar a mensagem e quer ver. Override do "near-bottom" tracker.
    proximoDoFundoRef.current = true;
    rolarFundo();
  }

  /* ── alternar modo bot ↔ humano (handoff Fase 2) ── */
  async function handleToggleModo() {
    if (!conversaId || togglandoModo) return;
    const modoAtual = conversa?.modo === "humano" ? "humano" : "bot";
    const novoModo = modoAtual === "humano" ? "bot" : "humano";
    setTogglandoModo(true);
    // Atualização otimista — backend reverte se 4xx
    setConversa((prev) => (prev ? { ...prev, modo: novoModo } : prev));
    try {
      await agentesService.putModoConversa("meta", conversaId, novoModo);
      toast.success(novoModo === "humano" ? "Você assumiu a conversa" : "Atendimento encerrado — conversa devolvida ao bot");
      onModoChange?.(conversaId, novoModo);
    } catch (e) {
      setConversa((prev) => (prev ? { ...prev, modo: modoAtual } : prev));
      toast.error(e?.response?.data?.error || "Erro ao alternar modo");
    } finally {
      setTogglandoModo(false);
    }
  }

  /* ── upload de mídia concluído → envia mensagem com media_id ── */
  async function handleMediaUploaded(mediaId, mimeType, _fileSize) {
    const tipo = mimeType?.startsWith("image/")    ? "image"
               : mimeType?.startsWith("video/")    ? "video"
               : mimeType?.startsWith("audio/")    ? "audio"
               : "document";
    try {
      const resultado = await enviarMidia(conversaId, { tipo, mediaId });
      handleMensagemEnviada(resultado);
    } catch {
      // erro tratado no MediaUploader
    }
  }

  /* ── Socket.IO ── */
  useEffect(() => {
    if (!socket || !conversaId) return;

    socket.emit("join-conversa", conversaId);

    function handleMensagemNew(payload) {
      if (!payload || payload.conversa_id !== conversaId) return;
      const nova = payload.mensagem ?? payload;

      setMensagens((prev) => {
        if (prev.some((m) => m.id === nova.id || (nova.wamid && m.wamid === nova.wamid))) return prev;
        // Substitui mensagem otimista (criada localmente ao enviar) pela real
        // quando o socket trouxer eco outbound com mesmo texto. Match: tipo+
        // direcao 'out' + texto extraído idêntico, dentro de janela de 30s.
        if (nova.direcao === "out") {
          const textoNova = nova.conteudo?.text?.body ?? nova.conteudo?.text ?? "";
          const idxOtimista = prev.findIndex((m) =>
            m._otimista &&
            m.direcao === "out" &&
            m.tipo === nova.tipo &&
            (m.conteudo?.text === textoNova || m.conteudo?.text?.body === textoNova),
          );
          if (idxOtimista !== -1) {
            const copia = [...prev];
            copia[idxOtimista] = nova;
            return copia;
          }
        }
        return [...prev, nova];
      });

      if (nova.direcao === "in") {
        setConversa((prev) =>
          prev
            ? { ...prev, janela_expira_em: new Date(Date.now() + 24 * 3600 * 1000).toISOString() }
            : prev
        );
        marcarLidoThrottled(conversaId);
      }

      // Auto-scroll só rola pro fundo se o usuário JÁ está perto do fundo.
      // Senão respeita a posição de leitura (igual WhatsApp/Telegram).
      if (proximoDoFundoRef.current) {
        rolarFundo();
      }
    }

    function handleMensagemStatus(payload) {
      if (!payload?.wamid) return;
      // Defensive: ignora status de outra conversa que vier pelo mesmo socket
      // (acontece se backend emitir broadcast no namespace ao invés de room).
      if (payload.conversa_id && payload.conversa_id !== conversaId) return;
      setMensagens((prev) =>
        prev.map((m) =>
          m.wamid === payload.wamid
            ? { ...m, status_meta: payload.status }
            : m
        )
      );
    }

    // Reconexão do Socket.IO cria um socket NOVO no servidor, sem as rooms —
    // sem re-join, eco outbound e status param de chegar e a bolha otimista
    // fica eterna no relóginho. Recarrega a página mais recente pra
    // reconciliar eventos perdidos durante a queda (o reload sem beforeId
    // substitui a lista, descartando otimistas órfãs).
    function handleReconnect() {
      socket.emit("join-conversa", conversaId);
      carregarMensagens();
    }

    socket.on("connect",         handleReconnect);
    socket.on("mensagem:new",    handleMensagemNew);
    socket.on("mensagem:status", handleMensagemStatus);

    return () => {
      socket.off("connect",         handleReconnect);
      socket.off("mensagem:new",    handleMensagemNew);
      socket.off("mensagem:status", handleMensagemStatus);
      socket.emit("leave-conversa", conversaId);
    };
  }, [socket, conversaId, rolarFundo, marcarLidoThrottled, carregarMensagens]);

  // Placeholder — nenhuma conversa selecionada
  if (!conversaId) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-surface-1/40 text-center px-6">
        <MessageSquare className="w-12 h-12 text-ink-5 mb-4" aria-hidden="true" />
        <p className="text-ink-4 text-sm font-medium">Selecione uma conversa</p>
        <p className="text-ink-5 text-xs mt-1">
          Escolha uma conversa na lista para visualizar o histórico
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-surface-1/40">
        <Loader2 className="w-6 h-6 animate-spin text-ink-5" aria-label="Carregando conversa" />
      </div>
    );
  }

  // Formata telefone E.164 -> "+55 65 9 9943-4063" (BR). Sem DDI 55, devolve raw.
  function formatarTelefoneBR(raw) {
    if (!raw) return "";
    const d = String(raw).replace(/\D/g, "");
    if (!d.startsWith("55")) return raw.startsWith("+") ? raw : `+${d}`;
    const ddd = d.slice(2, 4);
    const local = d.slice(4);
    if (local.length === 9) return `+55 ${ddd} ${local[0]} ${local.slice(1, 5)}-${local.slice(5)}`;
    if (local.length === 8) return `+55 ${ddd} ${local.slice(0, 4)}-${local.slice(4)}`;
    return `+${d}`;
  }

  const temNome  = !!conversa?.cliente_nome;
  const nome     = conversa?.cliente_nome || "Sem nome";
  const telefone = formatarTelefoneBR(conversa?.cliente_telefone || conversa?.wa_id || "");

  return (
    <div className="flex flex-col h-full bg-surface-1">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06] bg-base flex-shrink-0">
        {/* Botão voltar — visível apenas em mobile */}
        {onVoltar && (
          <button
            type="button"
            onClick={onVoltar}
            aria-label="Voltar para lista de conversas"
            className="flex md:hidden text-ink-3 hover:text-ink-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-1 -ml-1 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}

        {/* Avatar compacto — Meta Cloud API não fornece foto de perfil
            de contatos externos; usa inicial do nome como placeholder. */}
        <div
          className="w-8 h-8 rounded-full bg-brand-800 text-brand-200 flex items-center justify-center text-xs font-bold flex-shrink-0 select-none"
          aria-hidden="true"
          title={temNome ? nome : "Sem nome cadastrado"}
        >
          {(temNome ? nome[0] : telefone.replace(/\D/g, "").slice(-2, -1) || "?").toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-1 truncate leading-tight">{nome}</p>
          {telefone && (
            <p className="text-xs text-ink-4 truncate">{telefone}</p>
          )}
        </div>

        {/* Botão de handoff bot ↔ humano (Fase 2) */}
        <button
          type="button"
          onClick={handleToggleModo}
          disabled={togglandoModo}
          title={conversa?.modo === "humano" ? "Encerrar atendimento (devolve ao bot e sai da aba Atendimento)" : "Assumir conversa (pausa o agente)"}
          aria-label={conversa?.modo === "humano" ? "Encerrar atendimento" : "Assumir conversa"}
          className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
            conversa?.modo === "humano"
              ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"
              : "bg-surface-1 border-white/[0.06] text-ink-3 hover:bg-surface-2"
          }`}
        >
          {togglandoModo ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : conversa?.modo === "humano" ? (
            <CheckCircle2 className="w-3.5 h-3.5" />
          ) : (
            <Hand className="w-3.5 h-3.5" />
          )}
          {conversa?.modo === "humano" ? "Encerrar atendimento" : "Assumir conversa"}
        </button>

        <div className="flex-shrink-0">
          <JanelaIndicator janelaExpiraEm={conversa?.janela_expira_em} />
        </div>
      </div>

      {/* Lista de mensagens */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700 py-2"
        aria-label="Histórico de mensagens"
        aria-live="polite"
      >
        {/* Sentinel de paginação para cima */}
        <div ref={topo} className="h-1" aria-hidden="true" />

        {/* Indicador de carregando mais */}
        {carregando && mensagens.length > 0 && (
          <div className="flex justify-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-ink-5" aria-label="Carregando mensagens anteriores" />
          </div>
        )}

        {/* Estado vazio */}
        {!carregando && mensagens.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-center px-4">
            <p className="text-xs text-ink-5 italic">Nenhuma mensagem ainda.</p>
          </div>
        )}

        {/* Bolhas com separador de dia (estilo WhatsApp) */}
        {(() => {
          let ultimoDia = null;
          const nodes = [];
          for (const msg of mensagens) {
            const dia = chaveDia(msg.data_criacao);
            if (dia && dia !== ultimoDia) {
              nodes.push(
                <DiaSeparador
                  key={`dia-${dia}-${msg.id ?? msg.wamid ?? ""}`}
                  iso={msg.data_criacao}
                />
              );
              ultimoDia = dia;
            }
            nodes.push(
              <MessageBubble
                key={msg.id ?? msg.wamid}
                mensagem={msg}
                eu={msg.direcao === "out"}
                contaId={contaId}
                conversaId={conversaId}
                templatesMap={templatesMap}
              />
            );
          }
          return nodes;
        })()}
      </div>

      {/* ChatInput */}
      <ChatInput
        conversaId={conversaId}
        contaId={contaId}
        janelaAberta={conversa ? !!conversa.janela_expira_em && new Date(conversa.janela_expira_em) > new Date() : true}
        onSent={handleMensagemEnviada}
        onOpenMedia={()       => setModalAberto(MODAL.MEDIA)}
        onOpenTemplate={()    => setModalAberto(MODAL.TEMPLATE)}
        onOpenLocation={()    => setModalAberto(MODAL.LOCATION)}
        onOpenContact={()     => setModalAberto(MODAL.CONTACT)}
        onOpenInteractive={() => setModalAberto(MODAL.INTERACTIVE)}
      />

      {/* Modais condicionais */}
      {modalAberto === MODAL.MEDIA && (
        <MediaUploader
          contaId={contaId}
          onUploaded={handleMediaUploaded}
          onClose={() => setModalAberto(MODAL.NONE)}
        />
      )}

      {modalAberto === MODAL.TEMPLATE && (
        <TemplateSelectorModal
          contaId={contaId}
          conversaId={conversaId}
          conversa={conversa}
          onSent={handleMensagemEnviada}
          onClose={() => setModalAberto(MODAL.NONE)}
        />
      )}

      {modalAberto === MODAL.LOCATION && (
        <LocationSender
          conversaId={conversaId}
          onSent={handleMensagemEnviada}
          onClose={() => setModalAberto(MODAL.NONE)}
        />
      )}

      {modalAberto === MODAL.CONTACT && (
        <ContactSender
          conversaId={conversaId}
          onSent={handleMensagemEnviada}
          onClose={() => setModalAberto(MODAL.NONE)}
        />
      )}

      {modalAberto === MODAL.INTERACTIVE && (
        <InteractiveBuilder
          conversaId={conversaId}
          onSent={handleMensagemEnviada}
          onClose={() => setModalAberto(MODAL.NONE)}
        />
      )}
    </div>
  );
}
